/* generated using openapi-typescript-codegen -- do no edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { Collection } from '../models/Collection';
import type { CollectionDetail } from '../models/CollectionDetail';
import type { CollectionPermission } from '../models/CollectionPermission';
import type { CollectionQuery } from '../models/CollectionQuery';
import type { CollectionQueryDetail } from '../models/CollectionQueryDetail';
import type { CreateCollection } from '../models/CreateCollection';
import type { CreateCollectionQuery } from '../models/CreateCollectionQuery';
import type { CreateFolder } from '../models/CreateFolder';
import type { Folder } from '../models/Folder';
import type { FolderDetail } from '../models/FolderDetail';
import type { PaginationInfo } from '../models/PaginationInfo';
import type { QueryHistoryItem } from '../models/QueryHistoryItem';
import type { UpdateCollection } from '../models/UpdateCollection';
import type { UpdateCollectionQuery } from '../models/UpdateCollectionQuery';
import type { UpdateFolder } from '../models/UpdateFolder';
import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';
export class CollectionsService {
    /**
     * Get query history
     * Retrieve user's query execution history with pagination support
     * @returns any Query history retrieved successfully
     * @throws ApiError
     */
    public static getHistory({
        page = 1,
        limit = 20,
        dataSourceId,
        databaseName,
    }: {
        /**
         * Page number for pagination
         */
        page?: number,
        /**
         * Number of items per page
         */
        limit?: number,
        /**
         * Filter by data source ID
         */
        dataSourceId?: string,
        /**
         * Filter by database name
         */
        databaseName?: string,
    }): CancelablePromise<{
        /**
         * Array of query history items
         */
        data?: Array<QueryHistoryItem>;
        pagination?: PaginationInfo;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/queries/history',
            query: {
                'page': page,
                'limit': limit,
                'dataSourceId': dataSourceId,
                'databaseName': databaseName,
            },
            errors: {
                400: `Invalid request parameters`,
                401: `Unauthorized access`,
            },
        });
    }
    /**
     * List collections
     * Get paginated list of collections accessible to the user, including owned collections and shared collections.
     *
     * **Collection Access Types:**
     * 1. **Owned Collections**: Collections created by the user (user is the owner)
     * 2. **Shared Collections**: Collections shared with the user by other users (requires feature flag)
     * 3. **Public Collections**: Collections marked as public (accessible to all authenticated users with read-only access)
     *
     * **Shared Collections Feature:**
     * - Requires `enable_shared_collections` feature flag to be enabled for the user
     * - Collections shared with you will appear in this list with their respective permission levels
     * - Permission levels: `read`, `write`, or `admin`
     * - The `permissionLevel` field in the response indicates your access level for each collection
     *
     * **Response Includes:**
     * - Collections you own (permissionLevel: "owner" or highest available)
     * - Collections shared with you (if feature flag enabled)
     * - Public collections (permissionLevel: "read" unless you have explicit higher permissions)
     *
     * **Filtering Shared Collections:**
     * - Shared collections appear alongside owned collections
     * - Use the `owner` field in response to distinguish between owned and shared
     * - Collections are returned in order based on `orderIndex` and creation time
     *
     * @returns any Collections retrieved successfully
     * @throws ApiError
     */
    public static listCollections({
        page = 1,
        limit = 20,
        parentId,
        includeStats = false,
        search,
        tags,
    }: {
        /**
         * Page number for pagination
         */
        page?: number,
        /**
         * Number of items per page
         */
        limit?: number,
        /**
         * Filter by parent collection ID
         */
        parentId?: string,
        /**
         * Include statistics for each collection
         */
        includeStats?: boolean,
        /**
         * Search collections by name or description
         */
        search?: string,
        /**
         * Filter collections by tags (comma-separated)
         */
        tags?: string,
    }): CancelablePromise<{
        data?: Array<Collection>;
        pagination?: PaginationInfo;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/collections',
            query: {
                'page': page,
                'limit': limit,
                'parentId': parentId,
                'include_stats': includeStats,
                'search': search,
                'tags': tags,
            },
            errors: {
                400: `Bad Request`,
                401: `Unauthorized access`,
            },
        });
    }
    /**
     * Create collection
     * Create a new collection. The creating user becomes the owner and has full control over the collection.
     *
     * **Ownership and Sharing:**
     * - The user creating the collection becomes the permanent owner
     * - Owners can share their collections with other users if `enable_shared_collections` feature flag is enabled
     * - Owners can grant `read`, `write`, or `admin` permissions to other users
     * - Collections can be made public for read-only access by all authenticated users
     *
     * **After Creation:**
     * - Use `/api/collections/{collection_id}/permissions` endpoints to share the collection
     * - Use the `isPublic` field to make the collection publicly readable
     * - The owner always has full access and cannot lose ownership
     *
     * @returns any Collection created successfully
     * @throws ApiError
     */
    public static createCollection({
        requestBody,
    }: {
        requestBody: CreateCollection,
    }): CancelablePromise<{
        message?: string;
        id?: string;
        collection?: Collection;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/collections',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                401: `Unauthorized access`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict - Collection name already exists`,
            },
        });
    }
    /**
     * Get collection details
     * Get detailed information about a collection including queries and folders.
     *
     * **Access Requirements:**
     * - You can access this collection if you are:
     * - The owner of the collection
     * - A user with granted permissions (read, write, or admin) - requires feature flag
     * - Any authenticated user if the collection is public (read-only access)
     *
     * **Response Details:**
     * - The `permissionLevel` field indicates your access level: "owner", "admin", "write", or "read"
     * - The `owner` object shows who owns the collection
     * - For shared collections, you'll see the owner's information but can only perform actions based on your permission level
     *
     * **Permission-Based Access:**
     * - `read`: Can view collection details, queries, and folders
     * - `write`: Can view and modify collection contents
     * - `admin`: Can view, modify, and manage permissions
     * - `owner`: Full control including deletion and permission management
     *
     * @returns CollectionDetail Collection details retrieved successfully
     * @throws ApiError
     */
    public static getCollection({
        collectionId,
        includeStats = false,
    }: {
        /**
         * Collection identifier
         */
        collectionId: string,
        /**
         * Include statistics for the collection
         */
        includeStats?: boolean,
    }): CancelablePromise<CollectionDetail> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/collections/{collection_id}',
            path: {
                'collection_id': collectionId,
            },
            query: {
                'includeStats': includeStats,
            },
            errors: {
                401: `Unauthorized access`,
                403: `Forbidden`,
                404: `Collection not found`,
            },
        });
    }
    /**
     * Update collection
     * Update collection properties. Access level determines what can be modified.
     *
     * **Permission Requirements:**
     * - `owner`: Can update all properties including making collection public/private
     * - `admin`: Can update all properties except ownership transfer
     * - `write`: Can update name, description, icon, color, metadata, and orderIndex
     * - `read`: Cannot update (returns 403)
     *
     * **Shared Collections Behavior:**
     * - Users with `write` or `admin` permissions can modify shared collections
     * - Changes are immediately visible to all users with access
     * - The `isPublic` field can only be changed by the owner
     * - Parent collection changes require write access to both current and target parent
     *
     * @returns any Collection updated successfully
     * @throws ApiError
     */
    public static updateCollection({
        collectionId,
        requestBody,
    }: {
        /**
         * Collection identifier
         */
        collectionId: string,
        requestBody: UpdateCollection,
    }): CancelablePromise<{
        message?: string;
        id?: string;
    }> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/collections/{collection_id}',
            path: {
                'collection_id': collectionId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                401: `Unauthorized access`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict - Resource already exists`,
            },
        });
    }
    /**
     * Delete collection
     * Delete a collection and optionally its contents.
     *
     * **Permission Requirements:**
     * - `owner`: Can always delete the collection
     * - `admin`: Cannot delete (only owner can delete)
     * - `write`: Can delete with `force=true` if the collection is empty or force flag is used
     * - `read`: Cannot delete (returns 403)
     *
     * **Deletion Behavior:**
     * - Without `force=true`: Collection must be empty (no queries, folders, or subcollections)
     * - With `force=true`: Deletes collection and all its contents (queries, folders, subcollections)
     * - Deleting a collection removes all permissions granted to other users
     * - Shared users lose access immediately upon deletion
     *
     * **Cascading Effects:**
     * - All permissions are deleted when the collection is deleted
     * - Subcollections are deleted if force=true
     * - All queries and folders within are deleted if force=true
     *
     * **Important Notes:**
     * - Only the owner can permanently delete a collection
     * - Users with write permission + force flag can delete, but this is permanent
     * - Consider moving queries to another collection before deletion if you want to preserve them
     *
     * @returns void
     * @throws ApiError
     */
    public static deleteCollection({
        collectionId,
        force = false,
    }: {
        /**
         * Collection identifier
         */
        collectionId: string,
        /**
         * Force delete even if collection contains queries or folders
         */
        force?: boolean,
    }): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/collections/{collection_id}',
            path: {
                'collection_id': collectionId,
            },
            query: {
                'force': force,
            },
            errors: {
                400: `Collection not empty`,
                401: `Unauthorized access`,
                403: `Forbidden - insufficient permissions`,
                404: `Resource not found`,
            },
        });
    }
    /**
     * List queries in collection
     * Get paginated list of queries in a collection.
     *
     * **Permission Requirements:**
     * - Any permission level (`read`, `write`, `admin`, or `owner`) can list queries
     * - Public collections allow read access to all authenticated users
     *
     * @returns any Queries retrieved successfully
     * @throws ApiError
     */
    public static listCollectionQueries({
        collectionId,
        page = 1,
        limit = 20,
        folderId,
    }: {
        /**
         * Collection identifier
         */
        collectionId: string,
        /**
         * Page number for pagination
         */
        page?: number,
        /**
         * Number of items per page
         */
        limit?: number,
        /**
         * Filter by folder ID (null for root queries)
         */
        folderId?: string,
    }): CancelablePromise<{
        data?: Array<CollectionQuery>;
        pagination?: PaginationInfo;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/collections/{collection_id}/queries',
            path: {
                'collection_id': collectionId,
            },
            query: {
                'page': page,
                'limit': limit,
                'folderId': folderId,
            },
            errors: {
                400: `Invalid request parameters`,
                401: `Unauthorized access`,
                403: `Forbidden`,
                404: `Collection not found`,
            },
        });
    }
    /**
     * Create query in collection
     * Create a new query in a collection.
     *
     * **Permission Requirements:**
     * - Requires at least `write` permission on the collection
     * - Users with `read` permission cannot create queries (returns 403)
     * - Owner and admin users can always create queries
     *
     * @returns any Query created successfully
     * @throws ApiError
     */
    public static createCollectionQuery({
        collectionId,
        requestBody,
    }: {
        /**
         * Collection identifier
         */
        collectionId: string,
        requestBody: CreateCollectionQuery,
    }): CancelablePromise<{
        message?: string;
        id?: string;
        query?: CollectionQuery;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/collections/{collection_id}/queries',
            path: {
                'collection_id': collectionId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                401: `Unauthorized access`,
                403: `Forbidden`,
                404: `Resource not found`,
            },
        });
    }
    /**
     * Get query details
     * Get detailed information about a query in a collection.
     *
     * **Permission Requirements:**
     * - Any permission level (`read`, `write`, `admin`, or `owner`) can view query details
     * - Public collections allow read access to all authenticated users
     *
     * @returns CollectionQueryDetail Query details retrieved successfully
     * @throws ApiError
     */
    public static getCollectionQuery({
        collectionId,
        queryId,
    }: {
        /**
         * Collection identifier
         */
        collectionId: string,
        /**
         * Query identifier
         */
        queryId: number,
    }): CancelablePromise<CollectionQueryDetail> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/collections/{collection_id}/queries/{query_id}',
            path: {
                'collection_id': collectionId,
                'query_id': queryId,
            },
            errors: {
                401: `Unauthorized access`,
                403: `Forbidden - insufficient permissions`,
                404: `Resource not found`,
            },
        });
    }
    /**
     * Update query
     * Update a query in a collection.
     *
     * **Permission Requirements:**
     * - Requires at least `write` permission on the collection
     * - Users with `read` permission cannot update queries (returns 403)
     * - Owner, admin, and write users can update any query in the collection
     *
     * @returns any Query updated successfully
     * @throws ApiError
     */
    public static updateCollectionQuery({
        collectionId,
        queryId,
        requestBody,
    }: {
        /**
         * Collection identifier
         */
        collectionId: string,
        /**
         * Query identifier
         */
        queryId: number,
        requestBody: UpdateCollectionQuery,
    }): CancelablePromise<{
        message?: string;
        id?: number;
    }> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/collections/{collection_id}/queries/{query_id}',
            path: {
                'collection_id': collectionId,
                'query_id': queryId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Validation error`,
                401: `Unauthorized access`,
                403: `Forbidden - insufficient permissions`,
                404: `Not Found`,
            },
        });
    }
    /**
     * Delete query
     * Delete a query from a collection.
     *
     * **Permission Requirements:**
     * - Requires at least `write` permission on the collection
     * - Users with `read` permission cannot delete queries (returns 403)
     * - Owner, admin, and write users can delete any query in the collection
     *
     * @returns void
     * @throws ApiError
     */
    public static deleteCollectionQuery({
        collectionId,
        queryId,
    }: {
        /**
         * Collection identifier
         */
        collectionId: string,
        /**
         * Query identifier
         */
        queryId: number,
    }): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/collections/{collection_id}/queries/{query_id}',
            path: {
                'collection_id': collectionId,
                'query_id': queryId,
            },
            errors: {
                401: `Unauthorized access`,
                403: `Forbidden - insufficient permissions`,
                404: `Not Found`,
            },
        });
    }
    /**
     * Move query
     * Move a query to a different collection or folder
     * @returns any Query moved successfully
     * @throws ApiError
     */
    public static moveCollectionQuery({
        collectionId,
        queryId,
        requestBody,
    }: {
        /**
         * Collection identifier
         */
        collectionId: string,
        /**
         * Query identifier
         */
        queryId: number,
        requestBody: {
            /**
             * Target collection ID
             */
            targetCollectionId: string;
            /**
             * Target folder ID (optional)
             */
            targetFolderId?: string;
        },
    }): CancelablePromise<{
        message?: string;
        id?: number;
        newCollectionId?: string;
        newFolderId?: string | null;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/collections/{collection_id}/queries/{query_id}/move',
            path: {
                'collection_id': collectionId,
                'query_id': queryId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                401: `Unauthorized access`,
                403: `Forbidden - insufficient permissions`,
                404: `Not Found`,
            },
        });
    }
    /**
     * Get collection permissions
     * Retrieve the list of users who have been granted permissions to access this collection.
     *
     * **Prerequisites:**
     * - User must have the `enable_shared_collections` feature flag enabled (admin can set this via `/api/users/{user_id}/feature-flags`)
     * - Only the collection owner or users with `admin` permission level can view the permissions list
     * - The requesting user must have at least `admin` permission on the collection
     *
     * **Permission Levels:**
     * - `read`: Can view the collection and its contents (queries, folders)
     * - `write`: Can view and modify the collection (add/edit queries, create folders, update collection name)
     * - `admin`: Can view, modify, and manage permissions (grant/revoke access to other users)
     *
     * **Response Details:**
     * - Returns an array of all users with explicit permissions on this collection
     * - Does not include the owner (owner always has full access)
     * - For public collections, explicit permissions override the default public `read` access
     *
     * **Important Notes:**
     * - If the feature flag is disabled for the requesting user, this endpoint returns 403
     * - Permissions are cascading: when a user is deleted, their permissions are automatically removed
     * - When the owner is deleted, the entire collection and all permissions are deleted
     *
     * @returns any Permissions retrieved successfully
     * @throws ApiError
     */
    public static getCollectionPermissions({
        collectionId,
    }: {
        /**
         * Collection identifier
         */
        collectionId: string,
    }): CancelablePromise<{
        collectionId?: string;
        isPublic?: boolean;
        permissions?: Array<CollectionPermission>;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/collections/{collection_id}/permissions',
            path: {
                'collection_id': collectionId,
            },
            errors: {
                401: `Unauthorized access`,
                403: `Forbidden - insufficient permissions`,
                404: `Collection not found`,
            },
        });
    }
    /**
     * Grant or update collection permission
     * Grant or update permission for a user to access this collection. If the user already has permission, this will update their permission level.
     *
     * **Prerequisites:**
     * - Both the granting user and target user must have `enable_shared_collections` feature flag enabled
     * - The granting user must be either:
     * - The collection owner
     * - A user with `admin` permission level on this collection
     *
     * **Permission Levels:**
     * - `read`: User can view the collection, its queries, and folders but cannot make changes
     * - `write`: User can view and modify the collection contents (add/edit/delete queries, manage folders, rename collection, delete collection with force flag)
     * - `admin`: User has write permissions plus can manage other users' permissions (grant/revoke/update)
     *
     * **Workflow Example:**
     * ```
     * 1. Admin enables feature flag for users:
     * PUT /api/users/{owner_id}/feature-flags
     * Body: {"feature_flags": {"enable_shared_collections": true}}
     *
     * 2. Owner creates collection:
     * POST /api/collections
     * Body: {"name": "My Shared Collection"}
     *
     * 3. Owner grants permission to user (multiple ways supported):
     * POST /api/collections/{collection_id}/permissions
     * Body: {"userId": "user_id_here", "permissionLevel": "write"}
     * OR
     * Body: {"email": "user@example.com", "permissionLevel": "write"}
     * OR
     * Body: {"username": "target_username", "permissionLevel": "write"}
     *
     * 4. User can now access the collection based on their permission level
     * ```
     *
     * **Permission Transitions:**
     * - You can transition between any permission levels (read ↔ write ↔ admin)
     * - To change permission, simply call this endpoint again with the new level
     * - The response message will indicate if permission was "granted" (new) or "updated" (changed)
     *
     * **Restrictions:**
     * - Cannot grant permission to the collection owner (returns 400)
     * - Cannot grant invalid permission levels (returns 400)
     * - If target user's feature flag is disabled, they won't be able to access the collection even after permission is granted
     *
     * **Edge Cases:**
     * - For public collections: Explicit permissions override the default public `read` access
     * - Granting permission to a non-existent user returns 404
     * - If the owner's feature flag is disabled, no permission operations work (returns 403)
     *
     * **User Identification:**
     * - You can identify the target user by providing one of: `userId`, `email`, or `username`
     * - Priority order for resolution: userId > email > username (if multiple are provided)
     * - At least one identifier must be provided in the request
     * - Permission responses always include the user's email address for identification
     *
     * @returns any Permission granted successfully
     * @throws ApiError
     */
    public static grantCollectionPermission({
        collectionId,
        requestBody,
    }: {
        /**
         * Collection identifier
         */
        collectionId: string,
        requestBody: {
            /**
             * User ID to grant permission to (optional - provide one of userId, email, or username)
             */
            userId?: string;
            /**
             * Email address of the user to grant permission to (optional - provide one of userId, email, or username)
             */
            email?: string;
            /**
             * Username of the user to grant permission to (optional - provide one of userId, email, or username)
             */
            username?: string;
            /**
             * Permission level to grant
             */
            permissionLevel: 'read' | 'write' | 'admin';
        },
    }): CancelablePromise<{
        message?: string;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/collections/{collection_id}/permissions',
            path: {
                'collection_id': collectionId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                401: `Unauthorized access`,
                403: `Forbidden - insufficient permissions`,
                404: `Not Found`,
            },
        });
    }
    /**
     * Revoke collection permission
     * Revoke a user's permission to access this collection. After revocation, the user will no longer be able to access the collection unless it's public (in which case they retain read-only access).
     *
     * **Prerequisites:**
     * - The requesting user must have `enable_shared_collections` feature flag enabled
     * - The requesting user must be either:
     * - The collection owner
     * - A user with `admin` permission level on this collection
     *
     * **Effects of Revocation:**
     * - User immediately loses access to the collection
     * - All of the user's active sessions with the collection are terminated
     * - If the collection is public, the user retains read-only access as a public user
     * - If the collection is private, the user loses all access
     *
     * **Important Behaviors:**
     * - You cannot revoke your own permissions (owner cannot revoke from themselves)
     * - Revoking from a user who doesn't have permission returns 404
     * - Revoking when your feature flag is disabled returns 403
     *
     * **Cascading Effects:**
     * - If a user with `admin` permission is revoked, any permissions they granted remain intact
     * - The original owner retains the ability to manage all permissions regardless of who granted them
     *
     * **Common Workflow:**
     * ```
     * 1. List current permissions:
     * GET /api/collections/{collection_id}/permissions
     *
     * 2. Identify user to revoke:
     * Find userId in the permissions array
     *
     * 3. Revoke permission:
     * DELETE /api/collections/{collection_id}/permissions/{user_id}
     *
     * 4. User no longer has access (returns 403 if they try to access)
     * ```
     *
     * **Race Conditions:**
     * - If you grant and immediately revoke, the revoke takes precedence
     * - If multiple admins manage permissions simultaneously, the last operation wins
     *
     * @returns void
     * @throws ApiError
     */
    public static revokeCollectionPermission({
        collectionId,
        userId,
    }: {
        /**
         * Collection identifier
         */
        collectionId: string,
        /**
         * User ID whose permission should be revoked (UUID format)
         */
        userId: string,
    }): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/collections/{collection_id}/permissions/{user_id}',
            path: {
                'collection_id': collectionId,
                'user_id': userId,
            },
            errors: {
                401: `Unauthorized access`,
                403: `Forbidden - insufficient permissions`,
                404: `Not Found`,
            },
        });
    }
    /**
     * List folders in collection
     * Get paginated list of folders in a collection
     * @returns any Folders retrieved successfully
     * @throws ApiError
     */
    public static listCollectionFolders({
        collectionId,
        page = 1,
        limit = 20,
        parentFolderId,
    }: {
        /**
         * Collection identifier
         */
        collectionId: string,
        /**
         * Page number for pagination
         */
        page?: number,
        /**
         * Number of items per page
         */
        limit?: number,
        /**
         * Filter by parent folder ID
         */
        parentFolderId?: string,
    }): CancelablePromise<{
        data?: Array<Folder>;
        pagination?: PaginationInfo;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/collections/{collection_id}/folders',
            path: {
                'collection_id': collectionId,
            },
            query: {
                'page': page,
                'limit': limit,
                'parentFolderId': parentFolderId,
            },
            errors: {
                400: `Bad Request`,
                401: `Unauthorized access`,
                403: `Forbidden - insufficient permissions`,
                404: `Resource not found`,
            },
        });
    }
    /**
     * Create folder
     * Create a new folder in a collection
     * @returns any Folder created successfully
     * @throws ApiError
     */
    public static createFolder({
        collectionId,
        requestBody,
    }: {
        /**
         * Collection identifier
         */
        collectionId: string,
        requestBody: CreateFolder,
    }): CancelablePromise<{
        message?: string;
        id?: string;
        folder?: Folder;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/collections/{collection_id}/folders',
            path: {
                'collection_id': collectionId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                401: `Unauthorized access`,
                403: `Forbidden - insufficient permissions`,
                404: `Not Found`,
                409: `Conflict - Resource already exists`,
            },
        });
    }
    /**
     * Get folder details
     * Get detailed information about a folder including queries and subfolders
     * @returns FolderDetail Folder details retrieved successfully
     * @throws ApiError
     */
    public static getFolder({
        collectionId,
        folderId,
    }: {
        /**
         * Collection identifier
         */
        collectionId: string,
        /**
         * Folder identifier
         */
        folderId: string,
    }): CancelablePromise<FolderDetail> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/collections/{collection_id}/folders/{folder_id}',
            path: {
                'collection_id': collectionId,
                'folder_id': folderId,
            },
            errors: {
                401: `Unauthorized access`,
                403: `Forbidden - insufficient permissions`,
                404: `Resource not found`,
            },
        });
    }
    /**
     * Update folder
     * Update folder properties
     * @returns any Folder updated successfully
     * @throws ApiError
     */
    public static updateFolder({
        collectionId,
        folderId,
        requestBody,
    }: {
        /**
         * Collection identifier
         */
        collectionId: string,
        /**
         * Folder identifier
         */
        folderId: string,
        requestBody: UpdateFolder,
    }): CancelablePromise<{
        message?: string;
        id?: string;
    }> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/collections/{collection_id}/folders/{folder_id}',
            path: {
                'collection_id': collectionId,
                'folder_id': folderId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                401: `Unauthorized access`,
                403: `Forbidden - insufficient permissions`,
                404: `Resource not found`,
                409: `Conflict - Resource already exists`,
            },
        });
    }
    /**
     * Delete folder
     * Delete a folder. Use force=true to delete non-empty folders.
     * @returns void
     * @throws ApiError
     */
    public static deleteFolder({
        collectionId,
        folderId,
        force = false,
    }: {
        /**
         * Collection identifier
         */
        collectionId: string,
        /**
         * Folder identifier
         */
        folderId: string,
        /**
         * Force delete even if folder contains queries
         */
        force?: boolean,
    }): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/collections/{collection_id}/folders/{folder_id}',
            path: {
                'collection_id': collectionId,
                'folder_id': folderId,
            },
            query: {
                'force': force,
            },
            errors: {
                400: `Folder not empty`,
                401: `Unauthorized access`,
                403: `Forbidden - insufficient permissions`,
                404: `Resource not found`,
            },
        });
    }
}
